import { useMemo } from 'react';
import { useGetEstimateQuery } from '@/services/endpoints/audiences';
import type { CreditPricingPayload } from '@/services/endpoints/credits';
import { useWorkspace } from '@/features/workspaces/hooks';
import type { CampaignCreditChannel } from '../config';

type UseCreditValidityInputsArgs = {
  channel: CampaignCreditChannel;
  audienceId?: string;
  scheduleType: 'sendNow' | 'schedule';
  scheduleStart?: Date;
  /** WhatsApp template category (e.g. MARKETING). */
  templateCategory?: string;
};

export const useCreditValidityInputs = ({
  channel,
  audienceId,
  scheduleType,
  scheduleStart,
  templateCategory,
}: UseCreditValidityInputsArgs) => {
  const workspace = useWorkspace();
  const { data: estimate } = useGetEstimateQuery(
    { workspaceId: workspace.id, audienceId: audienceId ?? '' },
    { skip: !workspace.id || !audienceId }
  );

  const recipientCount = estimate?.size ?? 0;

  const at = useMemo(() => {
    if (scheduleType === 'schedule' && scheduleStart) {
      return scheduleStart.toISOString();
    }
    return new Date().toISOString();
  }, [scheduleType, scheduleStart]);

  const pricing = useMemo<CreditPricingPayload | null>(() => {
    if (!audienceId || recipientCount <= 0) {
      return null;
    }

    if (channel === 'whatsapp') {
      if (!templateCategory) {
        return null;
      }
      return {
        kind: 'WHATSAPP',
        category: templateCategory,
        quantity: recipientCount,
      };
    }

    return {
      kind: 'QUANTITY',
      quantity: recipientCount,
    };
  }, [audienceId, channel, recipientCount, templateCategory]);

  return {
    pricing,
    at,
    recipientCount,
  };
};

export const extractWhatsappTemplateCategory = (templateData?: Record<string, unknown>): string | undefined => {
  if (!templateData) {
    return undefined;
  }

  const raw =
    (templateData['template_category'] as string | undefined) ||
    (templateData['templateCategory'] as string | undefined) ||
    (templateData['category'] as string | undefined);

  return raw?.trim() || undefined;
};
